/**
 * 설계 가이드 관리 — 인덱스 설계 가이드 · 파티션 설계 가이드(2026-10-06 사용자 요구) · **튜닝 가이드**(2026-10-11).
 *
 * 튜닝 가이드는 같은 틀을 쓴다(규칙 묶음·매핑·작성중→배포·사본·파일 변환) + **대안 우선순위**(TuningPriorityEditor).
 * LiveSQL 이 주는 **범용 튜닝 가이드**는 목록에 읽기 전용으로 보이고, 고치려면 「내 가이드로 복사」 한다. 예전의 짧은 튜닝 규칙이
 * 남아 있으면 가이드로 옮기라고 알린다(옮기기 전까지는 지금처럼 늘 실린다).
 *
 *   · 종류마다 **여러 개**를 등록·수정한다(이름 · 설명 · 주제(세부주제)별 규칙 — 「적용 상황 → 원칙」 + 예외/대안, 2026-10-07)
 *   · 규칙마다 근거·원천·변경이력이 있다(규칙을 눌러야 보이는 세부 정보). 변경이력은 서버가 쓴다
 *   · **텍스트 문서(마크다운)로 내려받는다** — 근거·원천·이력까지 전부
 *   · **사본 보내기**(2026-10-07) — 다른 사용자(usrid)에게 작성중 사본을 건넨다. 받은 사람이 배포해야 그 사람의 설계에 실린다
 *   · **작성중 → 배포**(2026-10-07). 배포된 가이드만 설계 창에 뜨고, 배포 뒤에는 파일로 **완전 재구성할 수 없다** — 추가/보완만
 *     (한번 정한 가이드를 쉽게 갈아엎지 않게. 막는 것은 서버다 — 여기서는 선택지를 끈다)
 *   · **파일을 첨부하면 AI 가 규칙 목록으로 바꾼 초안**을 채워 준다 — 저장은 사람이 고친 뒤 한다
 *   · **DB(연결) · 스키마에 매핑**한다(여러 개). 매핑은 설계 창의 **기본 선택**을 정할 뿐 — 실릴지는 설계할 때 고른다
 *
 * 길이 상한(원칙 300자 · 규칙 40개 …)은 서버가 **거부**한다(자르면 뜻이 바뀐다). 화면은 넘기기 전에 막아 보여 줄 뿐이다.
 * 가이드는 자기 것만 보인다 — 남의 규칙이 내 설계안을 조용히 바꾸지 않게.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FiBookOpen, FiPlus, FiSave, FiTrash2, FiLoader, FiUpload, FiCpu, FiX, FiAlertCircle, FiDownload, FiSend, FiEdit2,
} from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { useConfirm } from '../ConfirmDialog';
import { usePrompt } from '../PromptDialog';
import { formatApiError } from '../../utils/formatters';
import { storedTime } from '../../utils/storedTime';
import { AI_ENABLED } from '../../config/features';
import {
    listDesignGuides, saveDesignGuide, deleteDesignGuide, convertDesignGuide, listAnalysisObjects, exportDesignGuide, publishDesignGuide,
    copyDesignGuide, copyBuiltinGuide, copySharedGuide, setGuideShared, migrateLegacyGuides, getCaseTopics,
} from '../../services/assistantClient';
import { saveTextFile } from '../../utils/desktopFile';
import WritingGuidePanel from './WritingGuidePanel';
import GuideDocView from './GuideDocView';
import GuideRulesEditor, { toFormSections, toPayload, guideProblems, GUIDE_LIMITS } from './GuideRulesEditor';
import TuningPriorityEditor, { emptyPriority, toPriorityForm, toPriorityPayload, priorityProblems } from './TuningPriorityEditor';
import memoWindow from '../common/memoWindow';

// 「SQL 표준」(2026-10-19) — SQL 품질 검사의 표준 규칙. 설계·튜닝 프롬프트에는 실리지 않는다(품질 검사의 기준에서 고른 규칙만)
const KINDS = ['index', 'partition', 'tuning', 'sqlStandard'];
const ACCEPT = '.txt,.md,.pdf,.pptx,.docx,.png,.jpg,.jpeg,.gif,.webp';

const blank = (kind) => ({
    id: null, kind, title: '', description: '', sections: toFormSections([]), mappings: [], active: true, sourceName: null, readOnly: false,
    status: 'draft', priority: emptyPriority(), builtin: false, publicGuide: false, shared: false,
});
/**
 * 「고쳤나」 의 잣대 — 화면 이름표(key)·이력·빈 예외 줄은 빼고 견준다. 이름표는 만들 때마다 새로 붙어서,
 * 그대로 견주면 창을 열자마자 「고친 내용이 사라집니다」 를 묻는다(실제로 그랬다).
 */
const snap = (f) => JSON.stringify({ ...f, sections: toPayload(f.sections) });
/** SQLite 의 UTC 일시('YYYY-MM-DD HH:MM:SS') → 내 시각 'YYYY-MM-DD HH:mm'. 못 읽으면 받은 그대로 */
const localAt = (s) => {
    if (!s) return '';
    const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${String(s).replace(' ', 'T')}Z`);
    if (Number.isNaN(d.getTime())) return String(s);
    const p2 = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

/** 서버 가이드 → 편집 상태 */
const fromGuide = (g) => ({
    id: g.id, kind: g.kind, title: g.title, description: g.description || '',
    sections: toFormSections(g.sections),
    mappings: (g.mappings || []).map(m => ({ connectionId: m.connectionId, schema: m.schema || '', connName: m.connName })),
    // 범용 가이드도 읽기 전용이지만 사유가 다르다(「더 새 판」 이 아니다) — builtin 으로 따로 잠근다
    active: g.active, sourceName: g.sourceName, readOnly: !!g.readOnly && !g.builtin && !g.publicGuide,
    status: g.status === 'draft' ? 'draft' : 'published', publishedAt: g.publishedAt || null,
    copiedFrom: g.copiedFrom || null,
    // 대안 우선순위(튜닝 가이드만) · 범용 가이드·공개 가이드(읽기 전용 — 복사해서 고친다). 공개 가이드도 builtin 과 같은 잠금을 쓴다
    priority: toPriorityForm(g.priority), builtin: !!g.builtin || !!g.publicGuide, publicGuide: !!g.publicGuide,
    // 내 가이드를 공개했는가(관리자만 켠다)
    shared: !!g.shared && !g.publicGuide,
    // 공개 가이드의 누가·언제·어느 판(마지막으로 고친 일시) — 보는 사람에게만 쓰인다
    sharedBy: g.sharedBy || null, sharedAt: g.sharedAt || null, version: g.version || null,
});

/** 매핑 한 줄 — 연결을 고르면 그 연결의 스키마 목록을 읽어 고르게 한다(못 읽으면 직접 적는다) */
function MappingRow({ value, connections, onChange, onRemove, t }) {
    const [schemas, setSchemas] = useState(null);
    useEffect(() => {
        if (!value.connectionId) { setSchemas(null); return undefined; }
        let alive = true;
        listAnalysisObjects({ connectionId: value.connectionId })
            .then((r) => { if (alive) setSchemas((r.schemas || []).map(s => s.name)); })
            .catch(() => { if (alive) setSchemas([]); });
        return () => { alive = false; };
    }, [value.connectionId]);
    return (
        <div className="tcf-row dg-mapRow">
            <select
                className="tcf-input ta-narrow" value={value.connectionId}
                onChange={(e) => onChange({ connectionId: e.target.value, schema: '' })}
            >
                <option value="">{t('assistant:designGuide.pickConnection')}</option>
                {connections.map(c => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
                {/* 지워진 연결의 매핑도 보여야 한다 — 고르지 못한 채로 사라지면 왜 기본 선택이 안 되는지 모른다 */}
                {value.connectionId && !connections.some(c => c.id === value.connectionId) ? (
                    <option value={value.connectionId}>{value.connName || value.connectionId}</option>
                ) : null}
            </select>
            {schemas && schemas.length ? (
                <select className="tcf-input ta-narrow" value={value.schema} onChange={(e) => onChange({ ...value, schema: e.target.value })}>
                    <option value="">{t('assistant:designGuide.wholeDb')}</option>
                    {schemas.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            ) : (
                <input
                    className="tcf-input ta-narrow" value={value.schema} placeholder={t('assistant:designGuide.wholeDb')}
                    onChange={(e) => onChange({ ...value, schema: e.target.value.toUpperCase() })}
                />
            )}
            <button type="button" className="tcf-iconBtn" title={t('common:ui.delete')} onClick={onRemove}><FiX size={11} /></button>
        </div>
    );
}
MappingRow.propTypes = {
    value: PropTypes.shape({ connectionId: PropTypes.string, schema: PropTypes.string, connName: PropTypes.string }).isRequired,
    connections: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

function DesignGuideModal({ open, initialKind, provider, onClose, restore, onView }) {
    const { t, i18n } = useTranslation();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const connections = useSelector(st => ((st.schema && st.schema.connections) || []).filter(c => c.dbType !== 'sqlite'));
    const [kind, setKind] = useState(initialKind || 'index');
    const [guides, setGuides] = useState([]);
    // 튜닝 탭 — 범용 가이드(읽기 전용) · 남아 있는 이전 규칙 수 · 처방 분류 목록(우선순위 편집용) · 우선순위 상한
    const [builtins, setBuiltins] = useState([]);
    // 공개 가이드(관리자가 공개한 것, 읽기 전용) · 내가 공개할 수 있는가(관리자)
    const [sharedGuides, setSharedGuides] = useState([]);
    const [canShare, setCanShare] = useState(false);
    const [legacyRules, setLegacyRules] = useState(0);
    const [fixTopics, setFixTopics] = useState([]);
    const [priLimits, setPriLimits] = useState({ values: 8, valueChars: 120 });
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(() => blank(initialKind || 'index'));
    const [saved, setSaved] = useState(() => snap(blank(initialKind || 'index')));
    const [busy, setBusy] = useState(false);
    const [converting, setConverting] = useState(false);
    const [convertInfo, setConvertInfo] = useState(null);   // { notes, rejected, usage }
    const [error, setError] = useState(null);
    const [showWriting, setShowWriting] = useState(false);
    /** 규칙이 있는 가이드에 파일을 올릴 때 — 추가/보완(기본) · 완전 재구성(작성중 가이드만) */
    const [convertMode, setConvertMode] = useState('augment');
    /** 세부 정보(근거·원천·이력)를 연 규칙 — 하나만 */
    const [openRule, setOpenRule] = useState('');
    /** 문서 보기(기본) ↔ 편집 — 가이드를 열면 문서처럼 읽히고, 「편집」 을 눌러야 편집 칸이 나온다. 아직 저장 안 된 새 가이드는 늘 편집 */
    const [docMode, setDocMode] = useState(true);
    const fileRef = useRef(null);

    useEffect(() => { if (open && initialKind) setKind(initialKind); }, [open, initialKind]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await listDesignGuides();
            setGuides(r.guides || []);
            setBuiltins(r.builtins || []);
            setSharedGuides(r.shared || []);
            setCanShare(!!r.canShare);
            setLegacyRules(r.legacyRules || 0);
            if (r.priorityLimits) setPriLimits(r.priorityLimits);
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setLoading(false); }
    }, [t]);
    useEffect(() => { if (open) load(); }, [open, load]);
    // 새로고침 뒤 — 보던 가이드를 한 번만 다시 고른다(App 이 workspaceLayout 에서 준 restore). 목록을 읽은 뒤에 찾고, 없으면(지워짐) 탭만 맞는다.
    // 되돌리기가 끝난 뒤에야 위치를 알린다 — 먼저 알리면 「고른 것 없음」 이 저장된 위치를 덮는다. 고치던 내용은 복원하지 않는다.
    const [viewReady, setViewReady] = useState(false);
    const [listed, setListed] = useState(false);
    useEffect(() => { if (open && !loading && !listed && (guides.length || builtins.length || !restore || !restore.id)) setListed(true); }, [open, loading, listed, guides, builtins, restore]);
    useEffect(() => {
        if (!open || viewReady) return;
        if (!(restore && restore.id)) { setViewReady(true); return; }
        if (!listed) return;
        const g = [...guides, ...builtins, ...sharedGuides].find(x => x.id === restore.id);
        if (g) { const next = fromGuide(g); setKind(g.kind); setForm(next); setSaved(snap(next)); }
        setViewReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 목록이 처음 왔을 때 한 번
    }, [open, listed]);
    useEffect(() => { if (open && viewReady && onView) onView({ id: form.id || null, step: kind }); }, [open, viewReady, form.id, kind, onView]);
    useEffect(() => {
        if (!open) return;
        getCaseTopics().then(r => setFixTopics((r.topics || []).filter(x => x.axis === 'fix'))).catch(() => setFixTopics([]));
    }, [open]);

    const dirty = snap(form) !== saved;
    const shown = useMemo(() => guides.filter(g => g.kind === kind), [guides, kind]);

    /** 고치던 것이 있으면 다른 가이드·새 가이드로 넘어가기 전에 묻는다 */
    const guardDirty = useCallback(async () => {
        if (!dirty) return true;
        return confirm({
            title: t('assistant:designGuide.discardTitle'),
            message: t('assistant:designGuide.discardConfirm'),
            confirmText: t('assistant:design.discard'),
            variant: 'danger',
        });
    }, [dirty, confirm, t]);

    const edit = useCallback(async (g) => {
        if (!(await guardDirty())) return;
        const next = g ? fromGuide(g) : blank(kind);
        setOpenRule('');
        setDocMode(true);
        setForm(next);
        setSaved(snap(next));
        setConvertInfo(null);
        setError(null);
    }, [guardDirty, kind]);

    const switchKind = useCallback(async (k) => {
        if (k === kind) return;
        if (!(await guardDirty())) return;
        setKind(k);
        const next = blank(k);
        setForm(next); setSaved(snap(next)); setConvertInfo(null);
    }, [kind, guardDirty]);

    const set = (patch) => setForm(f => ({ ...f, ...patch }));

    const save = useCallback(async () => {
        setBusy(true); setError(null);
        try {
            // 상태·배포 시각은 서버가 정한다 — 보내지 않는다
            const { readOnly, status, publishedAt, copiedFrom, priority, builtin, ...rest } = form;   // eslint-disable-line no-unused-vars
            const r = await saveDesignGuide({
                ...rest,
                // 대안 우선순위는 튜닝 가이드만 — 비었으면 null(지운다)
                ...(form.kind === 'tuning' ? { priority: toPriorityPayload(priority) } : {}),
                sections: toPayload(form.sections),
                mappings: form.mappings.filter(m => m.connectionId),
            });
            const next = fromGuide(r.guide);
            const g = r.guide;
            setOpenRule('');
            setForm(next); setSaved(snap(next));
            setDocMode(true);   // 저장하면 문서 보기로 돌아간다
            toast.success(t('assistant:designGuide.saved', { title: g.title }));
            if ((r.experienceHeld || []).length) toast.info(t('common:evo.heldToast', { count: r.experienceHeld.length }));
            await load();
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [form, load, t]);

    const remove = useCallback(async () => {
        if (!form.id) return;
        const ok = await confirm({
            title: t('assistant:designGuide.deleteTitle'),
            message: t('assistant:designGuide.deleteConfirm', { title: form.title }),
            confirmText: t('common:ui.delete'),
            variant: 'danger',
        });
        if (!ok) return;
        setBusy(true);
        try {
            await deleteDesignGuide(form.id);
            const next = blank(kind);
            setForm(next); setSaved(snap(next));
            await load();
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [form, kind, confirm, load, t]);

    /** 파일 → 초안. 지금 고치는 칸을 덮으므로, 이미 쓴 규칙이 있으면 먼저 묻는다 */
    const convert = useCallback(async (files) => {
        if (!files.length) return;
        // 규칙이 이미 있으면 고른 방식대로 — 추가/보완은 지금 규칙(고치던 것 포함)에 보태고, 완전 재구성은 갈아 끼운다(묻는다)
        const hasRules = form.sections.some(sec => sec.rules.some(x => x.principle.trim()));
        const mode = hasRules ? convertMode : 'rebuild';
        if (mode === 'rebuild' && hasRules) {
            const ok = await confirm({
                title: t('assistant:designGuide.convertTitle'),
                message: t('assistant:designGuide.convertOverwrite'),
                confirmText: t('assistant:designGuide.convertGo'),
                variant: 'danger',
            });
            if (!ok) return;
        }
        setConverting(true); setError(null); setConvertInfo(null);
        try {
            const r = await convertDesignGuide({
                files, kind, provider, lang: String((i18n && i18n.language) || 'ko').startsWith('en') ? 'en' : 'ko',
                mode, guideId: form.id || undefined, current: mode === 'augment' ? toPayload(form.sections) : undefined,
            });
            const d = r.draft || {};
            setForm(f => ({
                ...f,
                // 추가/보완은 이름·설명을 건드리지 않는다
                title: mode === 'rebuild' ? (d.title || f.title) : f.title,
                description: mode === 'rebuild' ? (d.description || f.description) : f.description,
                sections: toFormSections(d.sections),
                sourceName: f.sourceName || r.sourceName,
            }));
            setOpenRule('');
            setConvertInfo({
                notes: [...(r.notes || []), ...(d.notes ? [d.notes] : [])], rejected: d.rejected || [], usage: r.usage,
                empty: mode === 'rebuild' ? !(d.sections || []).length : !(d.augment && (d.augment.added || d.augment.supplemented.length)),
                augment: d.augment || null,
            });
        } catch (err) {
            const notes = (err.body && err.body.notes) || [];
            setError([formatApiError(err, t), ...notes].join(' · '));
        } finally {
            setConverting(false);
            if (fileRef.current) fileRef.current.value = '';
        }
    }, [form.sections, form.id, convertMode, kind, provider, i18n, confirm, t]);

    /** 사본 보내기 — 저장된 판을 보낸다(고치던 중이면 단추가 막힌다). 받는 사람은 usrid 로만 */
    const sendCopy = useCallback(async () => {
        if (!form.id) return;
        const v = await prompt({
            title: t('assistant:designGuide.copyTitle'),
            message: t('assistant:designGuide.copyMessage', { title: form.title }),
            defaultValue: '',
            placeholder: t('assistant:designGuide.copyPh'),
        });
        if (v === null) return;
        const to = String(v).split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
        if (!to.length) return;
        setBusy(true); setError(null);
        try {
            const r = await copyDesignGuide(form.id, to);
            if (r.sent.length) toast.success(t('assistant:designGuide.copySent', { count: r.sent.length, to: r.sent.map(x => x.to).join(', ') }));
            if (r.skipped.length) {
                toast.warning(t('assistant:designGuide.copySkipped', {
                    list: r.skipped.map(x => `${x.to}(${t(`assistant:designGuide.copySkip.${x.reason === 'SELF' ? 'self' : 'bad'}`)})`).join(', '),
                }));
            }
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [form.id, form.title, prompt, t]);

    /** 배포 — 되돌리지 않는다. 배포하면 설계 창에 뜨고, 그 뒤로는 파일로 완전 재구성할 수 없다 */
    const publish = useCallback(async () => {
        if (!form.id) return;
        const ok = await confirm({
            title: t('assistant:designGuide.publishTitle'),
            message: t('assistant:designGuide.publishConfirm', { title: form.title }),
            confirmText: t('assistant:designGuide.publish'),
        });
        if (!ok) return;
        setBusy(true);
        try {
            const r = await publishDesignGuide(form.id);
            const next = fromGuide(r.guide);
            setForm(next); setSaved(snap(next));
            setConvertMode('augment');
            toast.success(t('assistant:designGuide.publishedToast', { title: r.guide.title }));
            if ((r.experienceHeld || []).length) toast.info(t('common:evo.heldToast', { count: r.experienceHeld.length }));
            await load();
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [form.id, form.title, confirm, load, t]);

    /** 범용 가이드 → 내 가이드(작성중 사본). 고친 뒤 배포하고 연결에 매핑하면 그 연결에서는 내 가이드가 범용을 대신한다 */
    const copyBuiltin = useCallback(async () => {
        if (!form.builtin) return;
        setBusy(true); setError(null);
        try {
            const r = form.publicGuide ? await copySharedGuide(form.id) : await copyBuiltinGuide(form.id);
            const next = fromGuide(r.guide);
            setOpenRule('');
            setForm(next); setSaved(snap(next));
            setDocMode(false);   // 복사는 고치려고 하는 것 — 사본을 바로 편집으로 연다
            toast.success(t('assistant:tuningGuide.copied', { title: r.guide.title }));
            await load();
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [form.builtin, form.publicGuide, form.id, load, t]);

    /** 공개하기·해제(관리자, 배포된 내 가이드) — 남은 읽기만 하고 쓰려면 복사한다. 원본을 고쳐도 남의 결과는 안 바뀐다 */
    const toggleShared = useCallback(async (on) => {
        if (!form.id) return;
        setBusy(true); setError(null);
        try {
            const r = await setGuideShared(form.id, on);
            setForm(f => ({ ...f, shared: !!r.guide.shared }));
            toast.success(t(on ? 'assistant:designGuide.shared.on' : 'assistant:designGuide.shared.off', { title: r.guide.title }));
            await load();
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [form.id, load, t]);

    /** 이전 규칙(짧은 규칙 목록) → 튜닝 가이드 하나(작성중). 옮기면 이전 규칙은 꺼진다 — 새 가이드를 배포해야 다시 실린다 */
    const migrate = useCallback(async () => {
        const ok = await confirm({
            title: t('assistant:tuningGuide.migrateTitle'),
            message: t('assistant:tuningGuide.migrateConfirm', { count: legacyRules }),
            confirmText: t('assistant:tuningGuide.migrate'),
        });
        if (!ok) return;
        setBusy(true); setError(null);
        try {
            const r = await migrateLegacyGuides();
            const next = fromGuide(r.guide);
            setForm(next); setSaved(snap(next));
            toast.success(t('assistant:tuningGuide.migrated', { count: r.moved }));
            await load();
        } catch (err) {
            setError(formatApiError(err, t));
        } finally { setBusy(false); }
    }, [confirm, legacyRules, load, t]);

    /** 텍스트 문서(마크다운)로 내려받기 — 저장된 판을 서버가 옮겨 적는다(근거·원천·이력까지) */
    const download = useCallback(async () => {
        if (!form.id) return;
        try {
            const r = await exportDesignGuide(form.id);
            const safe = String(r.title || 'guide').replace(/[\\/:*?"<>|]+/g, '_').slice(0, 60);
            const out = await saveTextFile({ fileName: `${safe}_${r.kind}-guide.md`, content: r.text, kind: 'text' });
            if (out && !out.ok && !out.canceled) toast.error(out.detail || out.reason || t('assistant:design.downloadFailed'));
        } catch (err) {
            setError(formatApiError(err, t));
        }
    }, [form.id, t]);

    const closeGuarded = useCallback(async () => {
        if (!(await guardDirty())) return;
        onClose();
    }, [guardDirty, onClose]);

    const problems = guideProblems(form.sections);
    const ruleCount = problems.ruleCount;
    const isTuning = kind === 'tuning';
    const priProblems = isTuning ? priorityProblems(form.priority, priLimits) : {};
    const priBad = !!(priProblems.tooManyValues || priProblems.longValue || priProblems.badStop);
    // 범용 가이드는 읽기 전용 — 저장·삭제·매핑·배포가 없고 「내 가이드로 복사」 만 있다
    const locked = form.readOnly || form.builtin;
    // 문서 보기 — 저장된 가이드·범용·공개 가이드. 새 가이드(아직 id 없음)는 쓸 것이 없으니 바로 편집
    const viewing = docMode && !!(form.id || form.builtin);
    const docForm = viewing ? {
        ...form,
        mappings: form.mappings.map(m => ({ ...m, connName: m.connName || ((connections || []).find(c => c.id === m.connectionId) || {}).name })),
    } : form;

    return (
        <AppModal windowed
            open={open} onClose={closeGuarded} title={t('assistant:designGuide.title')}
            icon={<FiBookOpen size={14} />} className="tcase-modal dg-modal"
        >
            <div className="tcase-formWrap">
                <div className="tcf">
                    <div className="ta-tabs" role="tablist">
                        {KINDS.map(k => (
                            <button
                                type="button" key={k} role="tab" aria-selected={k === kind}
                                className={`ta-tab${k === kind ? ' ta-tabOn' : ''}`} onClick={() => switchKind(k)}
                            >
                                {t(`assistant:designGuide.kind.${k}`)} ({guides.filter(g => g.kind === k).length})
                            </button>
                        ))}
                    </div>
                    <div className="tcf-row dg-introRow">
                        <span className="tcf-hint">{t(isTuning ? 'assistant:tuningGuide.intro' : 'assistant:designGuide.intro')}</span>
                        {/* 규칙 작성 가이드 — 공용 문서(사용자별 아님). 규칙을 어떻게 쓰는지 */}
                        <button type="button" className={`tcf-btn${showWriting ? ' ta-histOn' : ''}`} onClick={() => setShowWriting(v => !v)}>
                            <FiBookOpen size={11} /> {t('assistant:designGuide.writing.open')}
                        </button>
                    </div>
                    {showWriting ? <WritingGuidePanel t={t} /> : null}
                    {/* 이전 규칙(예전의 짧은 튜닝 규칙)이 남아 있으면 — 지금도 실리고 있다는 것과 옮기는 길을 말한다 */}
                    {isTuning && legacyRules > 0 ? (
                        <div className="tcf-row dg-legacy">
                            <FiAlertCircle size={12} />
                            <span className="tcf-hint">{t('assistant:tuningGuide.legacy', { count: legacyRules })}</span>
                            <button type="button" className="tcf-btn" disabled={busy} onClick={migrate}>{t('assistant:tuningGuide.migrate')}</button>
                        </div>
                    ) : null}

                    <div className="dg-body">
                        {/* 목록 */}
                        <div className="dg-list">
                            <button type="button" className="tcf-btn" onClick={() => edit(null)}>
                                <FiPlus size={11} /> {t('assistant:designGuide.new')}
                            </button>
                            {loading ? <FiLoader size={12} className="tcf-spin" /> : null}
                            {!loading && !shown.length ? <div className="tcf-hint">{t('assistant:designGuide.empty')}</div> : null}
                            {shown.map(g => (
                                <button
                                    type="button" key={g.id}
                                    className={`dg-item${g.id === form.id ? ' dg-itemOn' : ''}`} onClick={() => edit(g)}
                                >
                                    <span className="dg-itemTitle">
                                        {g.title}
                                        <span className={g.status === 'draft' ? 'dg-status dg-statusDraft' : 'dg-status'}>
                                            {t(`assistant:designGuide.status.${g.status === 'draft' ? 'draft' : 'published'}`)}
                                        </span>
                                        {g.copiedFrom ? <span className="dg-status dg-statusCopy">{t('assistant:designGuide.copiedTag')}</span> : null}
                                        {g.shared ? <span className="dg-status dg-statusCopy">{t('assistant:designGuide.shared.tag')}</span> : null}
                                    </span>
                                    <span className="ta-meta">
                                        {t('assistant:designGuide.ruleCount', { count: g.rules.length })}
                                        {' · '}
                                        {g.mappings.length
                                            ? g.mappings.map(m => `${m.connName || m.connectionId}${m.schema ? `.${m.schema}` : ''}`).join(', ')
                                            : t('assistant:designGuide.noMapping')}
                                        {!g.active ? ` · ${t('assistant:designGuide.off')}` : ''}
                                    </span>
                                </button>
                            ))}
                            {/* 범용 가이드 — LiveSQL 이 주는 읽기 전용 문서(모든 사용자). 튜닝은 매핑된 내 가이드가 없는 작업에 기본으로 실리고, 설계는 고를 때만 */}
                            {builtins.some(g => g.kind === kind) ? <div className="dg-listHead">{t('assistant:tuningGuide.builtinHead')}</div> : null}
                            {builtins.some(g => g.kind === kind) ? builtins.filter(g => g.kind === kind).map(g => (
                                <button
                                    type="button" key={g.id}
                                    className={`dg-item${g.id === form.id ? ' dg-itemOn' : ''}`} onClick={() => edit(g)}
                                >
                                    <span className="dg-itemTitle">
                                        {g.title}
                                        <span className="dg-status dg-statusCopy">{t('assistant:tuningGuide.builtinTag')}</span>
                                    </span>
                                    <span className="ta-meta">{t('assistant:designGuide.ruleCount', { count: g.rules.length })}</span>
                                </button>
                            )) : null}
                            {/* 공개 가이드 — 관리자가 공개한 읽기 전용 가이드. 쓰려면 내 가이드로 복사한다 */}
                            {sharedGuides.some(g => g.kind === kind) ? <div className="dg-listHead">{t('assistant:designGuide.shared.head')}</div> : null}
                            {sharedGuides.filter(g => g.kind === kind).map(g => (
                                <button
                                    type="button" key={g.id}
                                    className={`dg-item${g.id === form.id ? ' dg-itemOn' : ''}`} onClick={() => edit(g)}
                                >
                                    <span className="dg-itemTitle">
                                        {g.title}
                                        <span className="dg-status dg-statusCopy">{t('assistant:designGuide.shared.tag')}</span>
                                    </span>
                                    <span className="ta-meta">
                                        {t('assistant:designGuide.ruleCount', { count: (g.rules || []).length })}
                                        {' · '}{t('assistant:designGuide.shared.version', { at: localAt(g.version) || '-' })}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* 편집 */}
                        <div className="dg-form">
                            {/* 단추 줄 — 맨 위에 붙어 스크롤되지 않는다(2026-10-15). 보기·편집 전환, 삭제·사본 보내기·내려받기·저장 */}
                            {form.builtin ? null : (
                            <div className="tcf-actions dg-actionBar">
                                {viewing ? (
                                    <button type="button" className="tcf-btn tcf-primary" onClick={() => setDocMode(false)}>
                                        <FiEdit2 size={11} /> {t('assistant:designGuide.doc.edit')}
                                    </button>
                                ) : null}
                                {!viewing && form.id ? (
                                    <button type="button" className="tcf-btn" onClick={() => setDocMode(true)} title={t('assistant:designGuide.doc.viewTip')}>
                                        <FiBookOpen size={11} /> {t('assistant:designGuide.doc.view')}
                                    </button>
                                ) : null}
                                {dirty ? <span className="ta-meta">{t('assistant:designGuide.doc.unsaved')}</span> : null}
                                <span className="dg-actionGap" />
                                {form.id ? (
                                    <button type="button" className="tcf-btn" disabled={busy} onClick={remove}>
                                        <FiTrash2 size={11} /> {t('common:ui.delete')}
                                    </button>
                                ) : null}
                                {/* 사본 보내기 — 저장된 판을. 받은 사람에게는 작성중 사본이 생긴다 */}
                                {form.id ? (
                                    <button type="button" className="tcf-btn" disabled={busy || dirty} onClick={sendCopy}
                                        title={t(dirty ? 'assistant:designGuide.downloadDirty' : 'assistant:designGuide.copyTip')}>
                                        <FiSend size={11} /> {t('assistant:designGuide.copy')}
                                    </button>
                                ) : null}
                                {/* 내려받기는 **저장된 판** — 고치던 것이 있으면 저장한 뒤에 받는다 */}
                                {form.id ? (
                                    <button type="button" className="tcf-btn" disabled={busy || dirty} onClick={download}
                                        title={t(dirty ? 'assistant:designGuide.downloadDirty' : 'assistant:designGuide.downloadTip')}>
                                        <FiDownload size={11} /> {t('assistant:design.download')}
                                    </button>
                                ) : null}
                                {viewing ? null : (
                                    <button
                                        type="button" className="tcf-btn tcf-primary"
                                        disabled={busy || converting || !dirty || !form.title.trim() || !ruleCount
                                            || problems.over || problems.incomplete || problems.tooMany || form.readOnly || priBad}
                                        onClick={save}
                                    >
                                        {busy ? <FiLoader size={11} className="tcf-spin" /> : <FiSave size={11} />}
                                        {t('common:action.save')}
                                    </button>
                                )}
                            </div>
                            )}
                            {error ? <div className="gm-dropped"><FiAlertCircle size={12} />{error}</div> : null}
                            {form.builtin ? (
                                <div className="tcf-row dg-builtinBar">
                                    <span className="tcf-hint">
                                        {t(form.publicGuide ? 'assistant:designGuide.shared.hint' : (isTuning ? 'assistant:tuningGuide.builtinHint' : 'assistant:designGuide.builtinHint'))}
                                        {form.publicGuide ? (
                                            <span className="dg-sharedMeta">
                                                {t('assistant:designGuide.shared.meta', {
                                                    by: form.sharedBy || t('assistant:designGuide.shared.byAdmin'),
                                                    at: localAt(form.sharedAt) || '-',
                                                    version: localAt(form.version) || '-',
                                                })}
                                            </span>
                                        ) : null}
                                    </span>
                                    <button type="button" className="tcf-btn tcf-primary" disabled={busy} onClick={copyBuiltin}>
                                        <FiPlus size={11} /> {t('assistant:tuningGuide.copyToMine')}
                                    </button>
                                </div>
                            ) : null}
                            {AI_ENABLED && !form.builtin && !viewing ? (
                                <div className="tcf-row">
                                    <input
                                        ref={fileRef} type="file" multiple accept={ACCEPT} hidden
                                        onChange={(e) => convert([...(e.target.files || [])])}
                                    />
                                    <button type="button" className="tcf-btn" disabled={converting} onClick={() => fileRef.current && fileRef.current.click()}>
                                        {converting ? <FiLoader size={11} className="tcf-spin" /> : <FiUpload size={11} />}
                                        {converting ? t('assistant:designGuide.converting') : t('assistant:designGuide.fromFile')}
                                    </button>
                                    <span className="ta-ai" title={t('assistant:designGuide.fromFileTip')}><FiCpu size={10} />{t('assistant:tableAnalysis.aiTag')}</span>
                                    <span className="tcf-hint">{t('assistant:designGuide.fromFileHint')}</span>
                                </div>
                            ) : null}
                            {/* 규칙이 있는 가이드에 파일을 올릴 때의 방식 — 배포된 가이드는 추가/보완만(서버도 막는다) */}
                            {AI_ENABLED && !form.builtin && !viewing && form.sections.some(sec => sec.rules.some(x => x.principle.trim())) ? (
                                <div className="tcf-row dg-convertMode" role="radiogroup">
                                    {['augment', 'rebuild'].map(md => {
                                        const locked = md === 'rebuild' && form.status === 'published';
                                        return (
                                            <label className="tcf-choice" key={md}
                                                title={t(locked ? 'assistant:designGuide.rebuildLocked' : `assistant:designGuide.convertModeTip.${md}`)}>
                                                <input type="radio" name="dg-convert-mode" checked={convertMode === md && !locked} disabled={locked || converting}
                                                    onChange={() => setConvertMode(md)} />
                                                {t(`assistant:designGuide.convertMode.${md}`)}
                                            </label>
                                        );
                                    })}
                                    {form.status === 'published' ? <span className="ta-meta">{t('assistant:designGuide.rebuildLocked')}</span> : null}
                                </div>
                            ) : null}
                            {convertInfo ? (
                                <div className="dg-convert">
                                    {convertInfo.empty ? <div className="gm-dropped"><FiAlertCircle size={12} />{t('assistant:designGuide.convertEmpty')}</div> : (
                                        <div className="tcf-hint">
                                            {convertInfo.augment
                                                ? t('assistant:designGuide.augmentDone', {
                                                    added: convertInfo.augment.added,
                                                    supplemented: convertInfo.augment.supplemented.join(', ') || '-',
                                                })
                                                : t('assistant:designGuide.convertDone')}
                                        </div>
                                    )}
                                    {convertInfo.notes.map(n => <div className="tcf-hint" key={n}>{n}</div>)}
                                    {convertInfo.rejected.length ? (
                                        <div className="gm-dropped">
                                            <FiAlertCircle size={12} />
                                            {t('assistant:designGuide.convertRejected', { count: convertInfo.rejected.length })}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            {/* 상태 — 작성중 가이드는 설계 창에 뜨지 않는다 */}
                            {form.builtin ? null : (
                            <div className="tcf-row dg-statusRow">
                                <span className={form.status === 'draft' ? 'dg-status dg-statusDraft' : 'dg-status'}>
                                    {t(`assistant:designGuide.status.${form.status === 'draft' ? 'draft' : 'published'}`)}
                                </span>
                                <span className="ta-meta">
                                    {form.status === 'draft' ? t('assistant:designGuide.draftHint')
                                        : t('assistant:designGuide.publishedHint', { at: storedTime(form.publishedAt) || '-' })}
                                </span>
                                {form.copiedFrom ? (
                                    <span className="ta-meta">
                                        {form.copiedFrom.usrid
                                            ? t('assistant:designGuide.copiedFrom', { usrid: form.copiedFrom.usrid, title: form.copiedFrom.title })
                                            : form.copiedFrom.shared
                                                ? t('assistant:designGuide.shared.copiedFromVer', { title: form.copiedFrom.title, version: localAt(form.copiedFrom.version) || '-' })
                                                : t('assistant:tuningGuide.copiedFromBuiltin', { title: form.copiedFrom.title })}
                                    </span>
                                ) : null}
                                {/* 공개하기 — 관리자, 배포된 내 가이드만. 남은 읽기만 하고 쓰려면 복사한다 */}
                                {canShare && form.id && form.status === 'published' ? (
                                    <label className="tcf-choice" title={t('assistant:designGuide.shared.toggleTip')}>
                                        <input type="checkbox" checked={!!form.shared} disabled={busy} onChange={(e) => toggleShared(e.target.checked)} />
                                        {t('assistant:designGuide.shared.toggle')}
                                    </label>
                                ) : null}
                                {form.id && form.status === 'draft' ? (
                                    <button type="button" className="tcf-btn" disabled={busy || dirty || form.readOnly} onClick={publish}
                                        title={t(dirty ? 'assistant:designGuide.publishDirty' : 'assistant:designGuide.publishTip')}>
                                        {t('assistant:designGuide.publish')}
                                    </button>
                                ) : null}
                            </div>
                            )}
                            {viewing ? (
                                <div className="dg-docWrap">
                                    <GuideDocView form={docForm} fixTopics={fixTopics} showMappings={!form.builtin} t={t} />
                                </div>
                            ) : (<>
                            <label className="tcf-field">
                                <span className="tcf-label">{t('assistant:designGuide.f.title')}</span>
                                <input className="tcf-input" value={form.title} maxLength={80} readOnly={form.builtin} onChange={(e) => set({ title: e.target.value })} />
                            </label>
                            <label className="tcf-field">
                                <span className="tcf-label">{t('assistant:designGuide.f.description')}</span>
                                <textarea className="tcf-input" rows={2} value={form.description} readOnly={form.builtin} onChange={(e) => set({ description: e.target.value })} />
                            </label>

                            <div className="tcf-label">
                                {t('assistant:designGuide.f.rules', { count: ruleCount, max: GUIDE_LIMITS.rules })}
                            </div>
                            <div className="tcf-hint">{t('assistant:designGuide.rulesHint')}</div>
                            {form.readOnly ? <div className="ta-gap">{t('assistant:designGuide.readOnly')}</div> : null}
                            <GuideRulesEditor
                                sections={form.sections} readOnly={locked} t={t}
                                onChange={(next) => set({ sections: next })}
                                openKey={openRule} onToggle={(k) => setOpenRule(o => (o === k ? '' : k))}
                            />
                            {problems.incomplete ? <div className="gm-dropped"><FiAlertCircle size={12} />{t('assistant:designGuide.incomplete')}</div> : null}
                            {problems.exampleLike ? (
                                <div className="dg-exampleWarn">{t('assistant:designGuide.exampleWarnCount', { count: problems.exampleLike })}</div>
                            ) : null}
                            {problems.tooMany ? <div className="gm-dropped"><FiAlertCircle size={12} />{t('assistant:designGuide.tooMany')}</div> : null}

                            {isTuning ? (
                                <TuningPriorityEditor
                                    value={form.priority} fixTopics={fixTopics} readOnly={locked} limits={priLimits} t={t}
                                    onChange={(p) => set({ priority: p })}
                                />
                            ) : null}

                            {form.builtin ? null : (<>
                            <div className="tcf-label">{t('assistant:designGuide.f.mappings')}</div>
                            <div className="tcf-hint">{t(isTuning ? 'assistant:tuningGuide.mappingHint' : 'assistant:designGuide.mappingHint')}</div>
                            {form.mappings.map((m, i) => (
                                <MappingRow
                                    // eslint-disable-next-line react/no-array-index-key -- 아직 저장 전인 줄은 고유한 키가 없다
                                    key={i} value={m} connections={connections} t={t}
                                    onChange={(v) => setForm(f => ({ ...f, mappings: f.mappings.map((x, j) => (j === i ? { ...v, connName: null } : x)) }))}
                                    onRemove={() => setForm(f => ({ ...f, mappings: f.mappings.filter((_, j) => j !== i) }))}
                                />
                            ))}
                            <button
                                type="button" className="tcf-btn"
                                onClick={() => setForm(f => ({ ...f, mappings: [...f.mappings, { connectionId: '', schema: '' }] }))}
                            >
                                <FiPlus size={11} /> {t('assistant:designGuide.addMapping')}
                            </button>

                            <label className="tcf-choice">
                                <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
                                {t('assistant:designGuide.f.active')}
                            </label>
                            </>)}
                            </>)}
                        </div>
                    </div>
                </div>
            </div>
        </AppModal>
    );
}

DesignGuideModal.propTypes = {
    open: PropTypes.bool,
    initialKind: PropTypes.oneOf(['index', 'partition', 'tuning', 'sqlStandard']),
    provider: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    /** 새로고침 전에 보던 가이드 { id } — 처음 열릴 때 한 번 다시 고른다 */
    restore: PropTypes.shape({ id: PropTypes.string }),
    /** 보는 위치가 바뀔 때 ({ id, step: 종류 }) — App 이 배치에 저장한다 */
    onView: PropTypes.func,
};
DesignGuideModal.defaultProps = { open: false, initialKind: 'index', provider: undefined };

// 작업 탭을 오갈 때 App 이 새 콜백을 넘겨도 창을 통째로 다시 그리지 않는다(common/memoWindow, 2026-10-26 — 탭 전환이 멈칫했다)
export default memoWindow(DesignGuideModal);
